import { useState, useMemo } from 'react'
import { withTheme } from 'emotion-theming'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { withTranslation, Link } from 'server/i18n'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Flex, Box } from 'reflexbox'
import { Formik } from 'formik'
import { useRouter } from 'next/router'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'

import { submitLeadForm } from 'helpers/middleware'

import Input from 'components/Input'
import Textarea from 'components/Textarea'
import Checkbox from 'components/Checkbox'
import Button from 'components/Button'
import Select from 'components/Select'
import QuestionIcon from 'public/icons/question.svg'
import CloseIcon from 'public/icons/close.svg'
import InquryLoader from 'public/icons/inquiry-loader.svg'

import style from './InquiryForm.style'

const InquiryFormStyled = styled.div(props => ({ ...style(props) }))
const theme = { breakpoints: ['360px', '728px', '1024px', '1280px', '1600px'] }

/* ---------- PHONE HELPERS (HR lokalni + međunarodni) ---------- */
const DEFAULT_COUNTRY = process.env.NEXT_PUBLIC_DEFAULT_PHONE_COUNTRY || 'HR'
const ALLOWED_CC = new Set(
    (process.env.NEXT_PUBLIC_ALLOWED_COUNTRY_CODES || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
)
// mapiranje CC -> ISO (za national parse s '0')
const ISO_BY_CC = new Map([
    ['+385', 'HR'],
    ['+49', 'DE'],
    ['+43', 'AT'],
    ['+41', 'CH'],
    ['+33', 'FR'],
    ['+34', 'ES'],
    ['+39', 'IT'],
    ['+44', 'GB'],
    ['+31', 'NL'],
    ['+32', 'BE']
])

const phoneOk = v => {
    let s = String(v || '').trim()
    // zadrži znamenke i +; normaliziraj 00 -> +
    s = s.replace(/[^\d+]/g, '')
    if (!s) return false
    if (s.startsWith('00')) s = `+${s.slice(2)}`

    const tryParse = (num, iso) => {
        try {
            const p = parsePhoneNumberFromString(num, iso)
            return p && p.isValid() ? p : null
        } catch {
            return null
        }
    }

    // 1) national format (vodeća '0'): probaj DEFAULT_COUNTRY pa sve allowed ISO
    if (s.startsWith('0')) {
        let p = tryParse(s, DEFAULT_COUNTRY)
        if (!p) {
            const isoCandidates = ALLOWED_CC.size
                ? [...ALLOWED_CC].map(cc => ISO_BY_CC.get(cc)).filter(Boolean)
                : [...new Set(ISO_BY_CC.values())]
            for (const iso of isoCandidates) {
                p = tryParse(s, iso)
                if (p) break
            }
        }
        if (!p) return false
        // ograniči na allowed CC ako je lista postavljena
        if (ALLOWED_CC.size && !ALLOWED_CC.has(`+${p.countryCallingCode}`)) return false
        return true
    }

    // 2) već ima '+': internacionalni format
    if (s.startsWith('+')) {
        const p = tryParse(s)
        if (!p) return false
        if (ALLOWED_CC.size && !ALLOWED_CC.has(`+${p.countryCallingCode}`)) return false
        return true
    }

    // 3) bez '+' ali počinje dopuštenim pozivnim (npr. "49…")
    const allowedNoPlus = [...ALLOWED_CC].map(cc => cc.replace('+', ''))
    const startsWithAllowed = allowedNoPlus.find(cc => s.startsWith(cc))
    if (startsWithAllowed) {
        const p = tryParse(`+${s}`)
        if (!p) return false
        if (ALLOWED_CC.size && !ALLOWED_CC.has(`+${p.countryCallingCode}`)) return false
        return true
    }

    // 4) inače – dvosmisleno, odbij (npr. "2242948453")
    return false
}

/* ---------- Normalizacija i pomoćne funkcije za imena ---------- */
const normalizeToAscii = s =>
    String(s || '')
        .trim()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/ł/g, 'l')
        .replace(/Ł/g, 'L')
        .replace(/ß/g, 'ss')
        .replace(/ø/g, 'o')
        .replace(/Ø/g, 'O')
        .replace(/æ/g, 'ae')
        .replace(/Æ/g, 'AE')
        .replace(/œ/g, 'oe')
        .replace(/Œ/g, 'OE')

const nameOk = v => {
    const s = String(v || '').trim()
    if (s.length < 2 || s.length > 50) return false
    return /^[A-Za-z' -]+$/.test(normalizeToAscii(s))
}

const SUSPICIOUS_NAME_TOKENS = new Set([
    'test',
    'testing',
    'tester',
    'asdf',
    'qwerty',
    'zxcv',
    'qwe',
    'qwer',
    'qwert',
    'abc',
    'abcd',
    'abcde',
    'lorem',
    'ipsum',
    'xxx',
    'xxxx',
    'xxxxx',
    'firstname',
    'lastname',
    'yourname',
    'name',
    'unknown',
    'none',
    'null',
    'n/a'
])

const containsSuspiciousNameToken = raw => {
    const ascii = normalizeToAscii(raw)
        .toLowerCase()
        .replace(/[^a-z]+/g, ' ')
        .trim()
    if (!ascii) return true
    return ascii.split(/\s+/).some(w => SUSPICIOUS_NAME_TOKENS.has(w))
}

const looksLikeGibberishName = raw => {
    const s = String(raw || '').trim()
    const ascii = normalizeToAscii(s)
    const letters = ascii.replace(/[^A-Za-z]/g, '')
    const len = letters.length
    if (len < 2) return true

    if (len >= 6) {
        const vowels = (letters.match(/[AEIOUY]/gi) || []).length
        const vowelRatio = vowels / len
        if (vowelRatio < 0.2) return true

        // streak istih slova
        let maxStreak = 1
        let streak = 1
        for (let i = 1; i < len; i++) {
            if (letters[i] === letters[i - 1]) {
                streak++
                if (streak > maxStreak) maxStreak = streak
            } else {
                streak = 1
            }
        }
        if (maxStreak >= 3) return true

        // 5+ suglasnika zaredom
        let cons = 0
        let maxCons = 0
        for (let i = 0; i < len; i++) {
            const ch = letters[i].toLowerCase()
            if ('aeiouy'.includes(ch)) {
                cons = 0
            } else {
                cons++
                if (cons > maxCons) maxCons = cons
            }
        }
        if (maxCons >= 5) return true

        // česte izmjene velikih/malih
        const caseChanges = (ascii.match(/([a-z][A-Z]|[A-Z][a-z])/g) || []).length
        if (len >= 8 && caseChanges / Math.max(1, len - 1) >= 0.35) return true

        // upper-run + visok udio velikih, osim Mc/Mac…
        const hasLower = /[a-z]/.test(ascii)
        const hasUpper = /[A-Z]/.test(ascii)
        const upperRun = /[A-Z]{5,}/.test(ascii)
        const upperRatio = (ascii.match(/[A-Z]/g) || []).length / len
        const macPrefix = /^M(ac|c)[A-Z]/.test(ascii)
        if (hasLower && hasUpper && upperRun && upperRatio >= 0.5 && !macPrefix) return true

        // nizak udio jedinstvenih slova za duga imena
        const uniqueRatio = new Set(letters.toLowerCase().split('')).size / len
        if (len >= 12 && uniqueRatio < 0.35) return true
    }
    return false
}
/* ---------------------------------------------------------------- */

const InquiryForm = ({ t, type, onPage, showDryMarinaVodice }) => {
    const [submitted, setSubmitted] = useState(false)
    const [quickTip, setQuickTip] = useState(true)
    const router = useRouter()
    const { executeRecaptcha } = useGoogleReCaptcha()

    const closeQuickTip = () => setQuickTip(false)

    const errorRequired = t('controls:error-required-field')
    const errorEmail = t('controls:error-invalid-email')

    const options = useMemo(() => {
        const base = [
            { value: '', label: '' },
            { value: 'buy', label: t('buying-yacht') },
            { value: 'sell', label: t('selling-yacht') },
            { value: 'manage', label: t('manage-yacht') }
        ]
        if (showDryMarinaVodice) {
            base.push({ value: 'dry-marina-vodice', label: t('dry-marina-vodice') })
        }
        return base
    }, [t, showDryMarinaVodice])

    if (submitted) {
        return (
            <InquiryFormStyled onPage={onPage}>
                <div className="thank-you-message">
                    <p>{t('thank-you-message')}</p>
                </div>
            </InquiryFormStyled>
        )
    }

    return (
        <InquiryFormStyled>
            <Formik
                initialValues={{
                    inquiryType: type || '',
                    firstName: '',
                    lastName: '',
                    emailAddress: '',
                    phoneNumber: '',
                    message: '',
                    privacyPolicy: false,
                    boatUrl: router.query.brochure ? `BROCHURE for ${router.query.boatID}` : router?.query?.boatID,
                    hp: ''
                }}
                validate={values => {
                    const badWord = /(viagra|casino|loan|porn|sex|seo|backlinks|bitcoin|crypto|forex)/i
                    const hasLink = /(https?:\/\/|www\.)/i

                    const errors = {}

                    if (!values.inquiryType) errors.inquiryType = errorRequired

                    if (!values.firstName) {
                        errors.firstName = errorRequired
                    } else if (
                        !nameOk(values.firstName) ||
                        looksLikeGibberishName(values.firstName) ||
                        containsSuspiciousNameToken(values.firstName)
                    ) {
                        errors.firstName = t('invalid-name') || 'Please enter first name'
                    }

                    if (!values.lastName) {
                        errors.lastName = errorRequired
                    } else if (
                        !nameOk(values.lastName) ||
                        looksLikeGibberishName(values.lastName) ||
                        containsSuspiciousNameToken(values.lastName)
                    ) {
                        errors.lastName = t('invalid-name') || 'Please enter last name'
                    }

                    if (!values.emailAddress) {
                        errors.emailAddress = errorRequired
                    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.emailAddress)) {
                        errors.emailAddress = errorEmail
                    }

                    if (!values.phoneNumber) {
                        errors.phoneNumber = errorRequired
                    } else if (!phoneOk(values.phoneNumber)) {
                        errors.phoneNumber = t('invalid-phone') || 'Please enter a valid phone number'
                    }

                    if (!values.message) {
                        errors.message = errorRequired
                    } else if (
                        values.message.trim().length < 12 ||
                        badWord.test(values.message) ||
                        hasLink.test(values.message)
                    ) {
                        errors.message =
                            t('invalid-message') || 'Please enter a natural message (min 12 chars, no links/spam terms)'
                    }

                    if (!values.privacyPolicy) errors.privacyPolicy = errorRequired
                    if (values.hp) errors.firstName = errorRequired // honeypot

                    return errors
                }}
                onSubmit={async (values, { setSubmitting }) => {
                    const token = executeRecaptcha ? await executeRecaptcha('lead_form') : null
                    const response = await submitLeadForm({ ...values, recaptchaToken: token })
                    if (Object.entries(response || {}).length) {
                        setSubmitting(false)
                        setSubmitted(true)
                    }
                    if (process.browser) {
                        window.dataLayer.push({
                            event: 'GAEvent',
                            eventCategory: 'Form Submit',
                            eventAction: 'Contact',
                            eventLabel: 'Send message'
                        })
                    }
                }}>
                {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                      setFieldTouched
                  }) => (
                    <form className="merkForm" onSubmit={handleSubmit} noValidate>
                        {/* honeypot */}
                        <input
                            type="text"
                            name="hp"
                            value={values.hp}
                            onChange={handleChange}
                            tabIndex="-1"
                            autoComplete="off"
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                left: '-10000px',
                                top: 'auto',
                                width: '1px',
                                height: '1px',
                                overflow: 'hidden'
                            }}
                        />

                        <div className="col-1">
                            <Box theme={theme} mb="1.25rem">
                                <Select
                                    id="inquiryType"
                                    label={t('inquiry-type')}
                                    name="inquiryType"
                                    onChange={handleChange}
                                    onBlur={() => setFieldTouched('inquiryType', true)}
                                    value={values.inquiryType}
                                    options={options}
                                    required
                                    error={errors.inquiryType && touched.inquiryType && errors.inquiryType}
                                />
                            </Box>

                            <Flex theme={theme} mb="1.25rem" justifyContent="space-between">
                                <Box theme={theme} width="48%">
                                    <Input
                                        id="firstName"
                                        label={t('first-name')}
                                        type="text"
                                        name="firstName"
                                        onChange={handleChange}
                                        onBlur={e => {
                                            handleBlur(e)
                                            setFieldTouched('firstName', true)
                                        }}
                                        value={values.firstName}
                                        required
                                        error={errors.firstName && touched.firstName && errors.firstName}
                                    />
                                </Box>

                                <Box theme={theme} width="48%">
                                    <Input
                                        id="lastName"
                                        label={t('last-name')}
                                        type="text"
                                        name="lastName"
                                        onChange={handleChange}
                                        onBlur={e => {
                                            handleBlur(e)
                                            setFieldTouched('lastName', true)
                                        }}
                                        value={values.lastName}
                                        required
                                        error={errors.lastName && touched.lastName && errors.lastName}
                                    />
                                </Box>
                            </Flex>

                            <Box theme={theme} mb="1.25rem">
                                <Input
                                    id="emailAddress"
                                    label={t('email-address')}
                                    type="email"
                                    name="emailAddress"
                                    onChange={handleChange}
                                    onBlur={e => {
                                        handleBlur(e)
                                        setFieldTouched('emailAddress', true)
                                    }}
                                    value={values.emailAddress}
                                    required
                                    error={errors.emailAddress && touched.emailAddress && errors.emailAddress}
                                />
                            </Box>

                            <Box theme={theme} mb="1.25rem">
                                <Input
                                    id="phoneNumber"
                                    label={t('phone-number')}
                                    type="text"
                                    name="phoneNumber"
                                    onChange={handleChange}
                                    onBlur={e => {
                                        handleBlur(e)
                                        setFieldTouched('phoneNumber', true)
                                    }}
                                    value={values.phoneNumber}
                                    required
                                    error={errors.phoneNumber && touched.phoneNumber && errors.phoneNumber}
                                />
                            </Box>
                        </div>

                        <div className="col-2">
                            <Box theme={theme} mb="1.25rem">
                                <Textarea
                                    id="message"
                                    label={t('message')}
                                    name="message"
                                    onChange={handleChange}
                                    onBlur={e => {
                                        handleBlur(e)
                                        setFieldTouched('message', true)
                                    }}
                                    required
                                    error={errors.message && touched.message && errors.message}
                                />
                            </Box>

                            <Box theme={theme} mb="1.25rem">
                                <Checkbox
                                    id="privacyPolicy"
                                    label={
                                        <>
                                            {t('privacy-policy-note')}{' '}
                                            <Link href="/privacy-policy">
                                                <a>{t('privacy-policy-link')}</a>
                                            </Link>
                                            {t('privacy-policy-rest')}
                                        </>
                                    }
                                    name="privacyPolicy"
                                    onChange={handleChange}
                                    onBlur={e => {
                                        handleBlur(e)
                                        setFieldTouched('privacyPolicy', true)
                                    }}
                                    value={values.privacyPolicy}
                                    checked={values.privacyPolicy}
                                    required
                                    error={errors.privacyPolicy && touched.privacyPolicy && errors.privacyPolicy}
                                />
                            </Box>

                            <Box theme={theme} mb="1.25rem">
                                <Button
                                    variation={`primary fullWidth ${isSubmitting ? 'submitting' : ''}`}
                                    type="submit"
                                    disabled={isSubmitting || submitted}>
                                    {isSubmitting ? <InquryLoader /> : t('controls:send-inquiry')}
                                </Button>
                            </Box>
                        </div>

                        {quickTip && (
                            <div className="QuickTip">
                                <div className="QuickTip__head">
                                    <QuestionIcon /> <span className="Title">{t('quick-tip')}</span>
                                    <button type="button" className="CloseButton" onClick={closeQuickTip}>
                                        <CloseIcon />
                                    </button>
                                </div>
                                <div className="QuickTip__content">
                                    <p>{t(`quick-tip-text${type ? `-${type}` : ''}`)}</p>
                                    <p>
                                        <em>{t(`quick-tip-eg${type ? `-${type}` : ''}`)}</em>
                                    </p>
                                </div>
                            </div>
                        )}
                    </form>
                )}
            </Formik>
        </InquiryFormStyled>
    )
}

InquiryForm.propTypes = {
    t: PropTypes.func.isRequired,
    type: PropTypes.string,
    onPage: PropTypes.bool,
    showDryMarinaVodice: PropTypes.bool
}
InquiryForm.defaultProps = { type: undefined, onPage: false, showDryMarinaVodice: false }

const EnhancedInquiryForm = withTranslation(['common', 'controls'])(withTheme(InquiryForm))
const InquiryFormWithCaptcha = props => (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
        <EnhancedInquiryForm {...props} />
    </GoogleReCaptchaProvider>
)
export default InquiryFormWithCaptcha
